<script setup>
import { ref, watch, onMounted } from 'vue';
import { CalendarDate, DateFormatter, getLocalTimeZone } from '@internationalized/date';
import { Calendar as CalendarIcon, ChevronUp, ChevronDown, Clock } from 'lucide-vue-next';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const props = defineProps({
    modelValue: { type: Date, default: () => new Date() },
    placeholder: { type: String, default: 'Pick a date and time' },
    disabled: { type: Boolean, default: false },
    class: { type: null, required: false }
});

const emit = defineEmits(['update:modelValue']);

const controlValue = ref(props.modelValue || new Date());

// Time input refs
const hourInput = ref('00');
const minuteInput = ref('00');

// Convert JS Date to CalendarDate
const toCalendarDate = (date) => {
    if (!date) return null;
    if (date instanceof Date) {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return new CalendarDate(year, month, day);
    }
    return date;
};

// Convert CalendarDate to JS Date
const toJSDate = (calendarValue) => {
    if (!calendarValue) return null;
    return calendarValue.toDate(getLocalTimeZone());
};

const dateValue = ref(toCalendarDate(controlValue.value));

// Format datetime for display
const formatDateTime = (date) => {
    if (!date) return '';
    const formatter = new DateFormatter('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short'
    });
    return formatter.format(date);
};

// Time picker functions
const padTime = (value) => String(value).padStart(2, '0');

const validateHour = () => {
    let hour = parseInt(hourInput.value) || 0;
    hour = Math.max(0, Math.min(23, hour));
    hourInput.value = padTime(hour);
    updateDateTime();
};

const validateMinute = () => {
    let minute = parseInt(minuteInput.value) || 0;
    minute = Math.max(0, Math.min(59, minute));
    minuteInput.value = padTime(minute);
    updateDateTime();
};

const incrementTime = (type) => {
    if (type === 'hour') {
        let hour = (parseInt(hourInput.value) + 1) % 24;
        hourInput.value = padTime(hour);
    } else if (type === 'minute') {
        let minute = (parseInt(minuteInput.value) + 1) % 60;
        minuteInput.value = padTime(minute);
    }
    updateDateTime();
};

const decrementTime = (type) => {
    if (type === 'hour') {
        let hour = parseInt(hourInput.value) - 1;
        if (hour < 0) hour = 23;
        hourInput.value = padTime(hour);
    } else if (type === 'minute') {
        let minute = parseInt(minuteInput.value) - 1;
        if (minute < 0) minute = 59;
        minuteInput.value = padTime(minute);
    }
    updateDateTime();
};

const handleHourKeydown = (e) => {
    if (e.key === 'ArrowUp') {
        e.preventDefault();
        incrementTime('hour');
    } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        decrementTime('hour');
    }
};

const handleMinuteKeydown = (e) => {
    if (e.key === 'ArrowUp') {
        e.preventDefault();
        incrementTime('minute');
    } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        decrementTime('minute');
    }
};

const updateDateTime = () => {
    if (dateValue.value) {
        const jsDate = toJSDate(dateValue.value);
        jsDate.setHours(parseInt(hourInput.value), parseInt(minuteInput.value));
        controlValue.value = jsDate;
    }
};

// Initialize time values from current date
const initializeTimeFromDate = (date) => {
    if (date instanceof Date) {
        hourInput.value = padTime(date.getHours());
        minuteInput.value = padTime(date.getMinutes());
    }
};

// Watch for date changes from calendar
watch(dateValue, (newDate) => {
    if (newDate) {
        updateDateTime();
    }
});

// Watch for control value changes to emit
watch(controlValue, (newValue) => {
    emit('update:modelValue', newValue);
});

// Watch for prop value changes
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue && newValue !== controlValue.value) {
            const date = newValue instanceof Date ? newValue : new Date(newValue);
            controlValue.value = date;
            dateValue.value = toCalendarDate(date);
            initializeTimeFromDate(date);
        }
    }
);

onMounted(() => {
    if (props.modelValue) {
        const date = props.modelValue instanceof Date ? props.modelValue : new Date(props.modelValue);
        controlValue.value = date;
        dateValue.value = toCalendarDate(date);
        initializeTimeFromDate(date);
    } else {
        // Initialize with current date/time
        const now = new Date();
        controlValue.value = now;
        dateValue.value = toCalendarDate(now);
        initializeTimeFromDate(now);
    }
});
</script>

<template>
    <Popover>
        <PopoverTrigger as-child>
            <Button
                :disabled="disabled"
                variant="outline"
                :class="cn('w-full justify-start text-left font-normal', !controlValue && 'text-muted-foreground', props.class)">
                <CalendarIcon class="mr-2 h-4 w-4" />
                <span v-if="controlValue">
                    {{ formatDateTime(controlValue) }}
                </span>
                <span v-else class="text-muted-foreground">
                    {{ props.placeholder }}
                </span>
                <Clock class="ml-auto h-4 w-4" />
            </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0" align="start">
            <Calendar v-model="dateValue" :disabled="disabled" initial-focus />
            <div class="border-t border-border">
                <div class="p-3">
                    <div class="flex items-center justify-center space-x-4">
                        <!-- Hour Input -->
                        <div class="flex flex-col items-center space-y-1">
                            <Label class="text-xs text-muted-foreground">Hour</Label>
                            <div class="flex items-center space-x-1">
                                <Button variant="ghost" size="sm" class="h-6 w-6 p-0 hover:bg-accent" @click="decrementTime('hour')">
                                    <ChevronDown class="h-3 w-3" />
                                </Button>
                                <input
                                    v-model="hourInput"
                                    type="text"
                                    maxlength="2"
                                    placeholder="00"
                                    class="w-8 h-7 text-center text-sm font-mono border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent"
                                    @blur="validateHour"
                                    @keydown="handleHourKeydown"
                                    @focus="$event.target.select()" />
                                <Button variant="ghost" size="sm" class="h-6 w-6 p-0 hover:bg-accent" @click="incrementTime('hour')">
                                    <ChevronUp class="h-3 w-3" />
                                </Button>
                            </div>
                        </div>

                        <!-- Separator -->
                        <div class="flex items-center justify-center pt-4">
                            <span class="text-sm font-mono text-muted-foreground">:</span>
                        </div>

                        <!-- Minute Input -->
                        <div class="flex flex-col items-center space-y-1">
                            <Label class="text-xs text-muted-foreground">Minute</Label>
                            <div class="flex items-center space-x-1">
                                <Button variant="ghost" size="sm" class="h-6 w-6 p-0 hover:bg-accent" @click="decrementTime('minute')">
                                    <ChevronDown class="h-3 w-3" />
                                </Button>
                                <input
                                    v-model="minuteInput"
                                    type="text"
                                    maxlength="2"
                                    placeholder="00"
                                    class="w-8 h-7 text-center text-sm font-mono border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent"
                                    @blur="validateMinute"
                                    @keydown="handleMinuteKeydown"
                                    @focus="$event.target.select()" />
                                <Button variant="ghost" size="sm" class="h-6 w-6 p-0 hover:bg-accent" @click="incrementTime('minute')">
                                    <ChevronUp class="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PopoverContent>
    </Popover>
</template>
